'use client';

import { useState, useEffect } from 'react';

interface ToolFeedbackWidgetProps {
  toolSlug: string;
}

type FeedbackState = 'initial' | 'positive' | 'negative' | 'submitted';

interface StoredFeedback {
  rating: 'positive' | 'negative';
  feedback?: string;
  timestamp: number;
}

/**
 * ToolFeedbackWidget Component
 *
 * A subtle feedback widget for tool pages that asks "Was this tool helpful?"
 * - Stores feedback in localStorage to prevent duplicate submissions
 * - Shows optional improvement text input for negative feedback
 * - Uses brand colors (#2813e8, #2cc3d1)
 */
export default function ToolFeedbackWidget({ toolSlug }: ToolFeedbackWidgetProps) {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>('initial');
  const [improvementText, setImprovementText] = useState('');
  const [isHydrated, setIsHydrated] = useState(false);

  const storageKey = `tool-feedback-${toolSlug}`;

  // Check localStorage on mount
  useEffect(() => {
    setIsHydrated(true);

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        // Validate JSON parses correctly before marking as submitted
        JSON.parse(stored) as StoredFeedback;
        setFeedbackState('submitted');
      }
    } catch {
      // localStorage not available or invalid data
    }
  }, [storageKey]);

  const handleFeedback = (isPositive: boolean) => {
    if (isPositive) {
      // Positive feedback - save and show thanks immediately
      saveFeedback('positive');
      setFeedbackState('submitted');
    } else {
      // Negative feedback - show improvement input
      setFeedbackState('negative');
    }
  };

  const handleSubmitImprovement = () => {
    saveFeedback('negative', improvementText);
    setFeedbackState('submitted');
  };

  const handleSkipImprovement = () => {
    saveFeedback('negative');
    setFeedbackState('submitted');
  };

  const saveFeedback = (rating: 'positive' | 'negative', feedback?: string) => {
    const data: StoredFeedback = {
      rating,
      feedback,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch {
      // localStorage not available
    }

    // Future: Could send to API endpoint here
    // fetch('/api/tool-feedback', {
    //   method: 'POST',
    //   body: JSON.stringify({ toolSlug, ...data }),
    // });
  };

  // Render placeholder during SSR to prevent CLS when widget mounts
  if (!isHydrated) {
    return (
      <div className="py-8 px-4" aria-hidden="true">
        <div className="h-10" />
      </div>
    );
  }

  // Already submitted feedback
  if (feedbackState === 'submitted') {
    return (
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
            <svg
              className="w-5 h-5 text-[#2cc3d1]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>Thanks for your feedback!</span>
          </div>
        </div>
      </div>
    );
  }

  // Negative feedback - show improvement input
  if (feedbackState === 'negative') {
    return (
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-md mx-auto bg-gray-50 rounded-lg p-6 border border-gray-200">
            <p className="text-sm text-gray-700 mb-3">
              What could we improve?
            </p>
            <textarea
              value={improvementText}
              onChange={(e) => setImprovementText(e.target.value)}
              placeholder="Your feedback helps us make better tools..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2cc3d1] focus:border-transparent resize-none"
              rows={3}
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleSubmitImprovement}
                disabled={!improvementText.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[#2813e8] rounded-md hover:bg-[#2813e8]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Submit
              </button>
              <button
                onClick={handleSkipImprovement}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Initial state - show thumbs up/down
  return (
    <div className="py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
          <span className="text-gray-600 text-sm">Was this tool helpful?</span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleFeedback(true)}
              className="group flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 hover:border-[#2cc3d1] hover:bg-[#2cc3d1]/5 transition-all duration-200"
              aria-label="Yes, this tool was helpful"
            >
              <span className="text-xl group-hover:scale-110 transition-transform">
                👍
              </span>
              <span className="text-sm text-gray-600 group-hover:text-[#2cc3d1]">
                Yes
              </span>
            </button>
            <button
              onClick={() => handleFeedback(false)}
              className="group flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 hover:border-[#2813e8] hover:bg-[#2813e8]/5 transition-all duration-200"
              aria-label="No, this tool was not helpful"
            >
              <span className="text-xl group-hover:scale-110 transition-transform">
                👎
              </span>
              <span className="text-sm text-gray-600 group-hover:text-[#2813e8]">
                No
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
